import type { ThemeConfig } from 'antd';
import { theme as antdTheme } from 'antd';

/**
 * Helper que lee una CSS variable de :root.
 * Asi los tokens de Ant Design se sincronizan con el archivo theme.css.
 */
function readCssVar(name: string): string {
  if (typeof window === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/**
 * Construye el ThemeConfig de Ant Design leyendo las CSS variables actuales.
 * Se llama cada vez que cambia el modo (claro/oscuro) para que Ant Design use
 * los nuevos colores. REGLA: los valores salen SIEMPRE de theme.css (var()).
 */
export function buildAntdTheme(mode: 'light' | 'dark'): ThemeConfig {
  const v = readCssVar;
  return {
    algorithm: mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: v('--color-primary') || '#0e6d78',
      colorLink: v('--color-primary-soft') || '#0e6d78',
      colorSuccess: v('--color-ok') || '#2e9467',
      colorWarning: v('--color-warn') || '#c78f2b',
      colorError: v('--color-crit') || '#c44f44',
      colorInfo: v('--color-primary-soft') || '#0e6d78',

      colorBgBase: v('--color-bg-base') || (mode === 'dark' ? '#1a212a' : '#ffffff'),
      colorBgLayout: v('--color-bg-layout') || (mode === 'dark' ? '#11161c' : '#f2f4f7'),
      colorBgContainer: v('--color-bg-container') || (mode === 'dark' ? '#1a212a' : '#ffffff'),
      colorBgElevated: v('--color-bg-elevated') || (mode === 'dark' ? '#1e2631' : '#ffffff'),

      colorText: v('--color-text-primary') || undefined,
      colorTextSecondary: v('--color-text-mid') || undefined,
      colorTextTertiary: v('--color-text-secondary') || undefined,
      colorTextQuaternary: v('--color-text-tertiary') || undefined,
      colorBorder: v('--color-border') || undefined,
      colorBorderSecondary: v('--color-border-card') || undefined,

      borderRadius: 10,
      borderRadiusLG: 16,
      borderRadiusSM: 6,

      fontFamily: v('--font-family') || undefined,
      fontSize: 14,
      controlHeight: 36,

      boxShadow: v('--shadow-md') || undefined,
      boxShadowSecondary: v('--shadow-lg') || undefined,
    },
    components: {
      Layout: {
        siderBg: v('--color-bg-sider') || '#ffffff',
        headerBg: v('--color-bg-header') || '#ffffff',
        bodyBg: v('--color-bg-layout') || undefined,
        headerHeight: 60,
        headerPadding: '0 24px',
      },
      Menu: {
        itemBg: 'transparent',
        itemSelectedBg: v('--color-primary-tint') || undefined,
        itemSelectedColor: v('--color-primary-soft') || undefined,
        itemHoverColor: v('--color-primary-soft') || undefined,
      },
      Card: {
        boxShadowTertiary: v('--shadow-sm') || 'none',
      },
      Table: {
        rowHoverBg: v('--color-row-hover') || undefined,
        headerBg: v('--color-bg-container') || undefined,
        borderColor: v('--color-border-row') || undefined,
      },
      Modal: {
        contentBg: v('--color-bg-elevated') || undefined,
        headerBg: v('--color-bg-elevated') || undefined,
        borderRadiusLG: 18,
      },
    },
  };
}
